import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { filter, Observable, Subject } from 'rxjs';
import { type Inbox } from '../../server/models/inbox.model';
import * as InboxGet from '../../server/routes/inboxes/get';
import * as InboxPost from '../../server/routes/inboxes/post';
import { generateId, generatePath } from '../../server/services/utils.service';
import type { FileElement } from '../../server/types/file-types';
import type {
  InboxFrontEndMode,
  InboxMode,
} from '../../server/types/message-types';
import { MessageService } from './message.service';
import { TaskService } from './task.service';

type BaseInbox = {
  orgId: string;
  _id: string;
  refId: string;
  time: number; // the sort time (including draft sort time)
  inboxMode: InboxFrontEndMode;
  draftMessage?: { body: string; files?: FileElement[]; title?: string };
};

export type TaskInboxObj = BaseInbox & {
  inboxMode: 'task';
  inbox: Inbox & { mode: 'task' }; // How the inbox is on the server
};

export type ThreadInboxObj = BaseInbox & {
  inboxMode: 'thread';
  inbox: Inbox & { mode: 'thread' }; // How the inbox is on the server
};

export type DirectInboxObj = BaseInbox & {
  inboxMode: 'direct';
  inbox: Inbox & { mode: 'direct' };
};

export type ChannelInboxObj = BaseInbox & {
  inboxMode: 'channel';
  inbox: Inbox & { mode: 'channel' };
};

export type SpaceInboxObj = BaseInbox & {
  inboxMode: 'space';
  inbox: Inbox & { mode: 'space' };
};

export type AnnouncementInboxObj = BaseInbox & {
  inboxMode: 'announcement';
  inbox: Inbox & { mode: 'announcement' };
};

export type TaskActionInboxObj = BaseInbox & {
  inboxMode: 'action';
  inbox: Inbox & { mode: 'action' };
};

export type TaskDraftInboxObj = BaseInbox & {
  inboxMode: 'taskDraft';
  inbox?: Inbox & { mode: 'task' };
};

export type ThreadDraftInboxObj = BaseInbox & {
  inboxMode: 'threadDraft';
  inbox?: undefined;
};

export type InboxItemType =
  | TaskInboxObj
  | ThreadInboxObj
  | DirectInboxObj
  | ChannelInboxObj
  | SpaceInboxObj
  | AnnouncementInboxObj
  | TaskActionInboxObj
  | TaskDraftInboxObj
  | ThreadDraftInboxObj;

type InboxObj = {
  orgId: string;
  authUserId: string;
  inboxes: InboxItemType[];
  archives: InboxItemType[];
  inboxSubject: Subject<InboxItemType>;
  inboxObservable: Observable<InboxItemType>;
};

@Injectable({ providedIn: 'root' })
export class InboxService {
  private http = inject(HttpClient);
  private taskService = inject(TaskService);
  private messageService = inject(MessageService);

  orgInboxes: InboxObj[] = [];

  constructor() {
    // We do this to prevent circular dependency (we don't want to import inbox
    // service into the message service if we are importing the message service
    // into the inbox service)
    const messageInboxSubject = new Subject<{
      inbox: Inbox;
      authUserId: string;
    }>();
    this.messageService.setInboxSubject(messageInboxSubject);
    messageInboxSubject.subscribe((i) => this.setInbox(i));

    const messageInboxDraftSubject = new Subject<{
      orgId: string;
      authUserId: string;
      refId: string;
      inboxMode: 'thread' | 'task';
    }>();
    this.messageService.setInboxDraftSubject(messageInboxDraftSubject);
    messageInboxDraftSubject.subscribe((draft) => this.addDraftToInbox(draft));
  }

  getOrgInbox({ orgId, authUserId }: { orgId: string; authUserId: string }) {
    let orgInbox = this.orgInboxes.find(
      (i) => i.orgId === orgId && i.authUserId === authUserId
    );

    if (!orgInbox) {
      const inboxSubject = new Subject<InboxItemType>();
      const inboxObservable = inboxSubject.asObservable();
      orgInbox = {
        orgId,
        authUserId,
        inboxes: [],
        archives: [],
        inboxSubject,
        inboxObservable,
      };
      this.orgInboxes.push(orgInbox);
    }

    return orgInbox;
  }

  /**
   * This function should only be used by the inbox page when it is navigated
   * to (i.e. on ngInit)
   */
  getCachedLatestInboxItems({
    orgId,
    authUserId,
    archive,
  }: {
    orgId: string;
    authUserId: string;
    archive: boolean;
  }): Observable<InboxItemType[]> {
    const inboxItemsSubject = new Subject<InboxItemType[]>();
    const inboxItemsObservable = inboxItemsSubject.asObservable();
    const orgInbox = this.getOrgInbox({ orgId, authUserId });
    const store = archive ? orgInbox.archives : orgInbox.inboxes;

    this.getInboxItems({ orgId, authUserId, archive }).subscribe({
      next: () => {
        // Items should be already inserted and ordered
        const inboxItems = store.slice(0, 20);
        inboxItemsSubject.next(inboxItems);
      },
      error: () => {
        const inboxItems = store.slice(0, 20);
        inboxItemsSubject.next(inboxItems);
      },
    });

    return inboxItemsObservable;
  }

  getInboxItems({
    orgId,
    authUserId,
    before,
    archive,
  }: {
    orgId: string;
    authUserId: string;
    before?: number;
    archive: boolean;
  }): Observable<InboxItemType[]> {
    const inboxItemsSubject = new Subject<InboxItemType[]>();
    const inboxItemsObservable = inboxItemsSubject.asObservable();

    const path = generatePath<InboxGet.ReqParams>('/api/orgs/:orgId/inboxes', {
      orgId,
    });
    const params: InboxGet.ReqQuery = {};
    if (archive) params.archived = '1';
    if (before) params.before = before;
    this.http.get<InboxGet.ResBody>(path, { params }).subscribe({
      next: ({ inboxJoins, requestTime }) => {
        // Always update the task service first to ensure that the task is ready #RaceCondition
        inboxJoins.forEach(({ task }) => {
          if (task) this.taskService.storeServerTaskData({ task });
        });

        inboxJoins.forEach((inboxJoin) => {
          const { task, thread, inbox, messages, messageSeen } = inboxJoin;
          this.setInbox({ inbox, authUserId });

          const messageSeens = messageSeen ? [messageSeen] : undefined;
          if (task)
            this.messageService.updateOrCreateThreadItem({
              authUserId,
              inbox,
              task,
              messages,
              messageSeens,
            });
          if (thread)
            this.messageService.updateOrCreateThreadItem({
              authUserId,
              inbox,
              thread,
              messages,
              messageSeens,
            });
        });
        const orgInbox = this.getOrgInbox({ orgId, authUserId });
        inboxItemsSubject.next(orgInbox.inboxes);
      },
      error: (err) => {
        const orgInbox = this.getOrgInbox({ orgId, authUserId });
        inboxItemsSubject.next(orgInbox.inboxes);
      },
    });

    return inboxItemsObservable;
  }

  sortByDate({
    orgId,
    authUserId,
    archive,
  }: {
    orgId: string;
    authUserId: string;
    archive: boolean;
  }): void {
    const orgInbox = this.getOrgInbox({ orgId, authUserId });
    const store = archive ? orgInbox.archives : orgInbox.inboxes;
    store.sort((a, b) => {
      if (a.time > b.time) return -1;
      else if (a.time < b.time) return 1;
      return 0;
    });
  }

  addDraftToInbox({
    orgId,
    authUserId,
    refId,
    inboxMode,
  }: {
    orgId: string;
    authUserId: string;
    refId: string;
    inboxMode: InboxMode;
  }) {
    const orgInbox = this.getOrgInbox({ orgId, authUserId });
    const draft: ThreadDraftInboxObj | TaskDraftInboxObj =
      inboxMode === 'thread'
        ? {
            orgId,
            _id: generateId(),
            refId,
            time: Date.now(),
            inboxMode: 'threadDraft',
          }
        : {
            orgId,
            _id: generateId(),
            refId,
            time: Date.now(),
            inboxMode: 'taskDraft',
          };
    orgInbox.inboxes.unshift(draft);
    this.sortByDate({ orgId, authUserId, archive: false });
    orgInbox.inboxSubject.next(draft);
  }

  updateInboxTimeForDraftMessage({
    orgId,
    authUserId,
    refId,
  }: {
    orgId: string;
    authUserId: string;
    refId: string;
  }) {
    const orgInbox = this.getOrgInbox({ orgId, authUserId });
    const inboxObj = orgInbox.inboxes.find(
      (i) =>
        (i.inboxMode === 'thread' ||
          i.inboxMode === 'task' ||
          i.inboxMode === 'taskDraft' ||
          i.inboxMode === 'threadDraft') &&
        i.refId === refId
    );
    if (!inboxObj) return;
    inboxObj.time = Date.now();
    orgInbox.inboxSubject.next(inboxObj);
  }

  setInbox({ inbox, authUserId }: { inbox: Inbox; authUserId: string }) {
    const { orgId, refId } = inbox;
    const orgInbox = this.getOrgInbox({ orgId, authUserId });
    let index = orgInbox.inboxes.findIndex((i) => i.refId === refId);
    let existing = orgInbox.inboxes[index];
    let existingInArchive = false;

    if (!existing) {
      index = orgInbox.archives.findIndex((i) => i.refId === refId);
      existing = orgInbox.archives[index];
      existingInArchive = !!existing;
    }

    if (index !== -1) {
      if (existing.inboxMode === 'task' || existing.inboxMode === 'thread') {
        if (existing.inbox.updatedAt > inbox.updatedAt) return; // existing is newer
      }
    }

    let newInboxObj: InboxItemType;

    const mode = inbox.mode;

    if (mode === 'task') {
      newInboxObj = {
        _id: inbox._id,
        refId,
        time: inbox.time,
        orgId,
        inboxMode: mode,
        inbox: { ...inbox, mode },
      };
    } else if (mode === 'thread') {
      newInboxObj = {
        _id: inbox._id,
        refId,
        time: inbox.time,
        orgId,
        inboxMode: mode,
        inbox: { ...inbox, mode },
      };
    } else if (mode === 'direct') {
      newInboxObj = {
        _id: inbox._id,
        refId,
        time: inbox.time,
        orgId,
        inboxMode: mode,
        inbox: { ...inbox, mode },
      };
    } else if (mode === 'channel') {
      newInboxObj = {
        _id: inbox._id,
        refId,
        time: inbox.time,
        orgId,
        inboxMode: mode,
        inbox: { ...inbox, mode },
      };
    } else if (mode === 'space') {
      newInboxObj = {
        _id: inbox._id,
        refId,
        time: inbox.time,
        orgId,
        inboxMode: mode,
        inbox: { ...inbox, mode },
      };
    } else if (mode === 'announcement') {
      newInboxObj = {
        _id: inbox._id,
        refId,
        time: inbox.time,
        orgId,
        inboxMode: mode,
        inbox: { ...inbox, mode },
      };
    } else if (mode === 'action') {
      newInboxObj = {
        _id: inbox._id,
        refId,
        time: inbox.time,
        orgId,
        inboxMode: mode,
        inbox: { ...inbox, mode },
      };
    } else {
      newInboxObj = {
        _id: inbox._id,
        refId,
        time: inbox.time,
        orgId,
        inboxMode: mode,
        inbox: { ...inbox, mode },
      };
    }

    if (existing) {
      if (existingInArchive && newInboxObj.inbox) {
        if (newInboxObj.inbox.archived) {
          orgInbox.archives.splice(index, 1, newInboxObj);
          this.sortByDate({ orgId, authUserId, archive: true });
        } else {
          // Move to inbox
          orgInbox.archives.splice(index, 1);
          orgInbox.inboxes.unshift(newInboxObj);
          this.sortByDate({ orgId, authUserId, archive: false });
        }
      } else {
        if (!newInboxObj.inbox?.archived) {
          orgInbox.inboxes.splice(index, 1, newInboxObj);
          this.sortByDate({ orgId, authUserId, archive: false });
        } else {
          // Move to archive
          orgInbox.inboxes.splice(index, 1);
          orgInbox.archives.unshift(newInboxObj);
          this.sortByDate({ orgId, authUserId, archive: true });
        }
      }
    } else {
      if (newInboxObj.inbox?.archived) {
        orgInbox.archives.unshift(newInboxObj);
        this.sortByDate({ orgId, authUserId, archive: true });
      } else {
        orgInbox.inboxes.unshift(newInboxObj);
        this.sortByDate({ orgId, authUserId, archive: false });
      }
    }

    orgInbox.inboxSubject.next(newInboxObj);
  }

  getInboxObservable({
    orgId,
    authUserId,
  }: {
    orgId: string;
    authUserId: string;
  }) {
    const orgInbox = this.getOrgInbox({ orgId, authUserId });
    return orgInbox.inboxObservable;
  }

  getInboxObj({
    orgId,
    authUserId,
    refId,
  }: {
    orgId: string;
    authUserId: string;
    refId: string;
  }) {
    return this.getOrgInbox({ orgId, authUserId }).inboxSubject.pipe(
      filter((i) => i.refId === refId)
    );
  }

  archive({
    orgId,
    refId,
    authUserId,
  }: {
    orgId: string;
    refId: string;
    authUserId: string;
  }) {
    const params: InboxPost.ReqParams = { orgId, refId };
    const body: InboxPost.ReqBody = { archivedAt: Date.now() };
    const path = generatePath<InboxPost.ReqParams>(
      '/api/orgs/:orgId/inboxes/:refId',
      params
    );

    this.http.post<InboxPost.ResBody>(path, body).subscribe(({ inbox }) => {
      this.setInbox({ inbox, authUserId });
    });
  }

  unarchive({
    orgId,
    refId,
    authUserId,
  }: {
    orgId: string;
    refId: string;
    authUserId: string;
  }) {
    const params: InboxPost.ReqParams = { orgId, refId };
    const body: InboxPost.ReqBody = { archivedAt: null };
    const path = generatePath<InboxPost.ReqParams>(
      '/api/orgs/:orgId/inboxes/:refId',
      params
    );

    this.http.post<InboxPost.ResBody>(path, body).subscribe(({ inbox }) => {
      this.setInbox({ inbox, authUserId });
    });
  }
}

export const threadInboxPathUrlFormat = '/org/:orgId/inbox/:mode/:refId';
export function generateInboxItemPath({
  orgId,
  refId,
  mode,
}: {
  orgId: string;
  refId: string;
  mode: InboxFrontEndMode;
}) {
  return generatePath<{
    orgId: string;
    refId: string;
    mode: InboxFrontEndMode;
  }>(threadInboxPathUrlFormat, { orgId, refId, mode });
}

export const threadInboxArchivePathUrlFormat =
  '/org/:orgId/inbox-archive/:mode:threadId';
export function generateInboxItemArchivePath({
  orgId,
  refId,
  mode,
}: {
  orgId: string;
  refId: string;
  mode: InboxFrontEndMode;
}) {
  return generatePath<{
    orgId: string;
    refId: string;
    mode: InboxFrontEndMode;
  }>(threadInboxArchivePathUrlFormat, { orgId, refId, mode });
}

export const taskPathUrlFormat = '/org/:orgId/task/:taskId';
export function generateTaskPath({
  orgId,
  taskId,
}: {
  orgId: string;
  taskId: string;
}) {
  return generatePath<{ orgId: string; taskId: string }>(taskPathUrlFormat, {
    orgId,
    taskId,
  });
}

export const taskThreadFocusPathUrlFormat = '/org/:orgId/task/:taskId/thread';
export function generateTaskThreadFocusPath({
  orgId,
  taskId,
}: {
  orgId: string;
  taskId: string;
}) {
  return generatePath<{ orgId: string; taskId: string }>(
    taskThreadFocusPathUrlFormat,
    { orgId, taskId }
  );
}

export const taskInboxPathUrlFormat = '/org/:orgId/inbox/task/:taskId';
export function generateTaskInboxPath({
  orgId,
  taskId,
}: {
  orgId: string;
  taskId: string;
}) {
  return generatePath<{ orgId: string; taskId: string }>(
    taskInboxPathUrlFormat,
    { orgId, taskId }
  );
}

export const taskInboxArchivePathUrlFormat =
  '/org/:orgId/inbox-archive/task/:taskId';

export function generateTaskInboxArchivePath({
  orgId,
  taskId,
}: {
  orgId: string;
  taskId: string;
}) {
  return generatePath<{ orgId: string; taskId: string }>(
    taskInboxArchivePathUrlFormat,
    { orgId, taskId }
  );
}

export const taskInboxThreadFocusPathUrlFormat =
  '/org/:orgId/inbox/task/:taskId/thread';
export function generateTaskInboxThreadFocusPath({
  orgId,
  taskId,
}: {
  orgId: string;
  taskId: string;
}) {
  return generatePath<{ orgId: string; taskId: string }>(
    taskInboxThreadFocusPathUrlFormat,
    { orgId, taskId }
  );
}

export const taskInboxArchiveThreadFocusPathUrlFormat =
  '/org/:orgId/inbox-archive/task/:taskId/thread';
export function generateTaskInboxArchiveThreadFocusPath({
  orgId,
  taskId,
}: {
  orgId: string;
  taskId: string;
}) {
  return generatePath<{ orgId: string; taskId: string }>(
    taskInboxArchiveThreadFocusPathUrlFormat,
    { orgId, taskId }
  );
}

export function generateInboxPath({ orgId }: { orgId: string }) {
  return '/org/' + orgId + '/inbox';
}

export function generateInboxViewPath({
  orgId,
  refId,
  mode,
  archive,
}: {
  orgId: string;
  refId: string;
  mode: InboxFrontEndMode;
  archive: boolean;
}) {
  if (mode === 'task')
    if (archive)
      return generateTaskInboxArchiveThreadFocusPath({ orgId, taskId: refId });
    else return generateTaskInboxThreadFocusPath({ orgId, taskId: refId });

  if (archive) return generateInboxItemArchivePath({ orgId, refId, mode });
  return generateInboxItemPath({ orgId, refId, mode });
}

export function getInboxRefId({ path }: { path: string }) {
  const pathSegments = path.split('?')[0].split('#')[0].split('/');
  if (!pathSegments[3].startsWith('inbox')) return;

  return pathSegments[5];
}
